import classNames from "classnames";
import { get, isEmpty, map } from "lodash";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import { Select } from "PFComponents/select/select";
import TrashIcon from "PFIcons/trash.svg";
import { CustomType, PureCustomValue } from "PFTypes";
import { Id } from "PFTypes/id";
import React, { useMemo } from "react";
import { Controller, ErrorOption, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CustomFieldData, SkillsFrameworkData } from "../../../../skills_frameworks.types";
import { FIELD_ARRAY_NAME } from "../custom_fields_section.consts";
import css from "./single_form_row.module.scss";

type FieldArrayEntryName = `${typeof FIELD_ARRAY_NAME}.${number}.${keyof CustomFieldData}`;

type SingleFormRowProps = {
  customTypes: CustomType[];
  customTypesOptions: DropdownOption[];
  rowIndex: number;
  onRemove: VoidFunction;
};

export const SingleFormRow = ({
  customTypes,
  customTypesOptions,
  rowIndex,
  onRemove
}: SingleFormRowProps) => {
  const { t } = useTranslation("skillsFrameworks", { keyPrefix: "edit" });
  const {
    control,
    formState: { errors },
    watch,
    resetField
  } = useFormContext<SkillsFrameworkData>();

  const values = watch();
  const customFieldEntryName: FieldArrayEntryName = `${FIELD_ARRAY_NAME}.${rowIndex}.customTypeId`;
  const valuesEntryName: FieldArrayEntryName = `${FIELD_ARRAY_NAME}.${rowIndex}.values`;

  const thisRowCustomFieldId = get(values, customFieldEntryName);
  const selectedCustomField = useMemo(
    () => customTypes.find(({ id }) => id === thisRowCustomFieldId),
    [customTypes, thisRowCustomFieldId]
  );

  const usedCustomTypesIds = map(values.customFields, "customTypeId").filter((id) => id !== null);
  const options = useMemo(() => {
    const customFieldsUsedInOtherRows = usedCustomTypesIds.filter((id) => id !== thisRowCustomFieldId);
    return customTypesOptions.filter(({ item }) => !customFieldsUsedInOtherRows.includes(item));
  }, [customTypesOptions, thisRowCustomFieldId, usedCustomTypesIds]);

  const isValueDisabled = !selectedCustomField;

  return (
    <span className={css.row}>
      <Controller
        name={customFieldEntryName}
        control={control}
        render={({ field }) => (
          <Select
            // @ts-ignore
            label={rowIndex === 0 ? t("customFieldHeader") : null}
            kind="bordered"
            value={options.find(({ item }) => item === field.value)?.displayElement}
            controlledValue
            options={options}
            error={(get(errors, field.name) as unknown as ErrorOption)?.message}
            handleChange={(newValue: Id) => {
              field.onChange(newValue);
              resetField(valuesEntryName, { defaultValue: [] });
            }}
            className={css.customFieldSelect}
          />
        )}
      />
      <Controller
        name={valuesEntryName}
        control={control}
        rules={{
          required: thisRowCustomFieldId !== null && t("errors.required")
        }}
        render={({ field }) => {
          const otherCustomFieldsWithValues = values.customFields
            .filter(({ customTypeId }) => customTypeId !== selectedCustomField?.id)
            .filter(({ values }) => !isEmpty(values));

          return (
            <CustomValuesEditField
              qaIdPrefix={`skills-framework-${valuesEntryName}`}
              label={rowIndex === 0 ? t("valueHeader") : ""}
              kind="bordered"
              tip=""
              customType={selectedCustomField ?? null}
              disabled={isValueDisabled}
              values={map(field.value, ({ id, value }) => ({
                id,
                text: String(value),
                type: selectedCustomField?.name
              }))}
              errors={(get(errors, field.name) as unknown as ErrorOption)?.message}
              handleChange={(newCustomValues: PureCustomValue[]) => {
                const newValuesFormData = newCustomValues.map(({ id, text }) => ({ id, value: text }));
                field.onChange(newValuesFormData);
              }}
              connectedFields={otherCustomFieldsWithValues}
              forceUnlocked
              classes={{
                root: css.valueSelect,
                autoSelect: classNames({ [css.valueSelectDisabled]: isValueDisabled })
              }}
              letCreate={false}
            />
          );
        }}
      />
      <TrashIcon
        width={16}
        height={16}
        className={classNames(css.trashIcon, css.centerWithMargin, { [css.first]: rowIndex === 0 })}
        onClick={onRemove}
      />
    </span>
  );
};
